import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useActiveSubtab } from "./SubtabUtils";
import {
  ButtonVariant,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TabsList,
  Text,
} from "@merge-api/merge-javascript-shared";
import { ChevronDown } from "lucide-react";
import clsx from "clsx";

export type Subtab = {
  label: JSX.Element | string;
  destination?: string;
  icon?: React.ReactElement;
  disabled?: boolean;
};

type Props = {
  /**
   * The big black title, required
   */
  preTitleContent?: string | JSX.Element;

  /**
   * The big black title, required
   */
  title: string | JSX.Element;

  /**
   * A description or short subtitle that appears below the title if present
   */
  subtitle?: string | JSX.Element;

  /**
   * If present, renders content to the far right of the title
   */
  titleRightElement?: JSX.Element;

  /**
   * If present, renders content to the far right of the subtitle
   */
  subtitleRightElement?: JSX.Element;

  /**
   * Provides tab titles & icons & components that appear as subnavigation
   * under the title/description
   */
  subtabs?: Array<Subtab>;

  /**
   * If present, renders action buttons or controls aligned to the right of the header
   */
  buttons?: JSX.Element;

  /**
   * Set when the title interpolates a user-chosen / customer-supplied name; masks the heading in PostHog
   */
  titleSensitive?: boolean;
};

const HeaderBar = ({
  preTitleContent,
  title,
  subtitle,
  titleRightElement,
  subtitleRightElement,
  subtabs,
  buttons,
  titleSensitive,
}: Props) => {
  // Only changes if the subtabs/location does
  const activeSubtab = useActiveSubtab(subtabs);
  const history = useHistory();

  // Make sure the rendered content in here doesn't change when other data does
  const subtabElements = useMemo(
    () =>
      subtabs && (
        <div>
          <Tabs
            value={activeSubtab?.destination}
            onChange={(_, tab) => history.push(tab as string)}
            className="nav-lg-show"
            variant="button"
          >
            <TabsList>
              {subtabs.map((subtab) => (
                <Tab value={subtab.destination} key={subtab.destination}>
                  <div className="flex flex-row gap-x-1 items-center justify-start">
                    {subtab.icon} {subtab.label}
                  </div>
                </Tab>
              ))}
            </TabsList>
          </Tabs>
          <div className="nav-lg-hide">
            <Menu
              ButtonProps={{
                children: (
                  <div className="flex flex-row gap-x-1 items-center justify-start">
                    {activeSubtab?.icon} {activeSubtab?.label} <ChevronDown size={16} />
                  </div>
                ),
                variant: ButtonVariant.SecondaryGray,
              }}
              menuPlacement="bottom-start"
            >
              {subtabs.map((subtab) => (
                <MenuItem
                  onClick={() => history.push(subtab.destination ?? "")}
                  disabled={subtab == activeSubtab}
                  key={subtab.destination}
                >
                  <div className="flex flex-row gap-x-1 items-center justify-start">
                    {subtab.icon} {subtab.label}
                  </div>
                </MenuItem>
              ))}
            </Menu>
          </div>
        </div>
      ),
    [activeSubtab, subtabs],
  );

  return (
    <div className="flex flex-col pt-10 pb-5">
      {preTitleContent && <div className="mb-4">{preTitleContent}</div>}
      <div
        className={clsx(
          "flex flex-row justify-between items-center gap-4",
          subtitle || subtitleRightElement ? "mb-3" : "mb-5",
        )}
      >
        {/* When titleSensitive is set the heading interpolates a user-chosen name and is thus masked at the heading */}
        <Text variant="h2" className={titleSensitive ? "ph-no-capture" : undefined}>
          {title}
        </Text>
        <div className="self-end">{titleRightElement}</div>
      </div>

      {(subtitle || subtitleRightElement) && (
        <div className="flex flex-row justify-between items-center gap-4 mb-5">
          <Text as="div">{subtitle}</Text>
          <div className="self-end">{subtitleRightElement}</div>
        </div>
      )}

      {(subtabElements || buttons) && (
        <div className="flex flex-row justify-between items-center mb-5">
          {subtabElements}
          {buttons}
        </div>
      )}
      <div className="h-0 border-t border-gray-10" />
    </div>
  );
};

export default HeaderBar;
